import {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import AdmicityTableV2 from '../shared-components/Table/V2/AdmicityTableV2';
import {formatDate} from '../utility/dateUtil';
import FILTER_OPERATIONS from '../constants/filterOperations';
import useNotification from '../utility/hooks/useNotification';
import {useLocation, useNavigate} from 'react-router-dom';
import {useGetApprovalsByApprovalPersonIdQuery, useSubmitApprovalMutation, useLazyGetApprovalHistoryByRequestIdQuery}
    from '../api/services/formApprovalsService';
import {Box, Button, Stack, styled, TextField, Typography} from '@mui/material';
import {COMPACT_TABLE_PROPS} from '../pages/SchoolDashboard/Profiles/ProfileTabs/FormsTab/formsTableStyle';
import useIndentUnderTab from '../pages/SchoolDashboard/Profiles/ProfileTabs/FormsTab/useIndentUnderTab';
import {OUTLINED_BOX_SX, TopLineAroundTab} from '../pages/SchoolDashboard/Profiles/ProfileTabs/FormsTab/tabOutline';
import AdmicityStatus from '../shared-components/AdmicityStatus';
import AdmicityDialog from './AdmicityDialog';
import {APPROVAL_STATUSES, getApprovalStatusInfo} from '../constants/FormApprovalStatuses';
import {FORM_STATUSES} from '../constants/formStatuses';
import {FORM_REQUEST_STATUSES} from '../constants/formRequestStatuses';
import DateRangePicker from '../shared-components/Table/V2/components/columnFilters/DateRangePicker';
import Tooltip from '@mui/material/Tooltip';
import withPaginationV2 from '../hoc/pagination/withPaginationV2';

const StyledCell = styled('div')({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: '24px',
});

const CustomCell = ({cell}) => {
    const value = cell.getValue();
    return (
        <Tooltip title={value} arrow>
            <StyledCell>{value}</StyledCell>
        </Tooltip>
    );
};
CustomCell.propTypes = {cell: PropTypes.object.isRequired};

const withApprovalWidths = ({column}, base, isBody = false) => {
    // Maria, 18 October 2026: the Form column takes the spare width; the others are only as wide as their contents
    // When the table has to shrink, the Form column narrows, down to 160px, with long names wrapping, and the space
    // between the other columns narrows. Those columns stay on one line, and wrap only on a small screen.
    if (column.id === 'form') return {sx: {...base.sx, width: 'auto !important', minWidth: '160px !important', ...(isBody ? {whiteSpace: 'normal'} : {})}};
    // with a little more space between them
    if (['status', 'submitted', 'stage', 'approved'].includes(column.id)) {
        // the space between them narrows with the screen, from 32px down to 12px, before anything wraps
        return {sx: {...base.sx, width: '1% !important', pr: 'clamp(12px, 2.2vw, 32px)', whiteSpace: {xs: 'normal', md: 'nowrap'}}};
    }
    // the buttons column only as wide as its buttons
    if (column.id === 'mrt-row-actions') return {sx: {...base.sx, width: '1% !important', minWidth: 'auto !important', whiteSpace: 'nowrap'}};
    return base;
};

const ApprovalFormsTable = ({employeeId, inProfile = false}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const {showInfoNotification, showErrorNotification} = useNotification();

    const Table = useMemo(
        () =>
            withPaginationV2(AdmicityTableV2, {
                func: useGetApprovalsByApprovalPersonIdQuery,
                props: {employeeId},
            }),
        [employeeId]
    );

    const [fetchHistory, {
        data: historyData,
        isFetching: isHistoryFetching,
        isError: isHistoryError,
        error: historyError
    }] =
        useLazyGetApprovalHistoryByRequestIdQuery();
    const [submitApproval] = useSubmitApprovalMutation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [note, setNote] = useState('');
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [selectedAction, setSelectedAction] = useState(null);
    // AD-2779: a note typed and then cancelled is not carried into the next Approval or Rejection Note box.
    const closeNoteBox = () => {
        setDialogOpen(false);
        setNote('');
    };
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [historyRows, setHistoryRows] = useState([]);
    useMemo(() => {
        if (historyData) {
            setHistoryRows(Array.isArray(historyData) ? historyData : (historyData.items ?? []));
        }
    }, [historyData]);
    const handleAction = async (status) => {
        if (!selectedAssignment?.request) return;
        const result = await submitApproval({
            formId: selectedAssignment.stage.formId,
            assignmentId: selectedAssignment.id,
            status,
            note,
            employeeId,
        });

        if (result.error) {
            showErrorNotification(`Failed to ${status.toLowerCase()} form.`);
        } else {
            showInfoNotification(`Form ${status.toLowerCase()} successfully.`);
            setDialogOpen(false);
            setNote('');
        }
    };

    const columns = useMemo(
        () => [
            {accessorKey: 'stage.name', header: 'Approval Stage', filterFn: FILTER_OPERATIONS.CONTAINS},
            {
                id: 'stageProgress',
                header: 'Approval Progress',
                accessorFn: (row) => `Stage ${row.stageNumber} / ${row.stageCount}`,
                enableColumnFilter: false,
            },
            {
                accessorKey: 'status',
                header: 'Approval Status',
                // eslint-disable-next-line react/prop-types
                Cell: ({cell: {getValue}}) => <AdmicityStatus {...getApprovalStatusInfo(getValue())} />,
                filterVariant: 'select',
                filterFn: FILTER_OPERATIONS.EQUALS,
                filterSelectOptions: Object.values(APPROVAL_STATUSES).map((s) => ({value: s, label: s})),
            },
            {accessorKey: 'request.formName', header: 'Form Name', filterFn: FILTER_OPERATIONS.CONTAINS},
            {
                id: 'request.editedBy.fullname',
                accessorFn: (row) => row?.request?.editedBy?.fullname ?? '',
                header: 'Submitted By',
                filterFn: FILTER_OPERATIONS.CONTAINS
            },
            {
                accessorKey: 'request.editedAt',
                header: 'Submitted At',
                Cell: ({cell}) => (cell.getValue() ? formatDate(cell.getValue()) : ''),
                Filter: DateRangePicker,
                filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE,
            },
            {accessorKey: 'approvedBy', header: 'Approved By', filterFn: FILTER_OPERATIONS.CONTAINS},
            {
                accessorKey: 'approvedAt',
                header: 'Approved At',
                Cell: ({cell}) => (cell.getValue() ? formatDate(cell.getValue()) : ''),
                Filter: DateRangePicker,
                filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE,
            },
            {
                accessorKey: 'approvalNote',
                header: 'Approval Note',
                filterFn: FILTER_OPERATIONS.CONTAINS,
                Cell: CustomCell
            },
        ],
        []
    );

    const historyColumns = useMemo(
        () => [
            {accessorKey: 'stage.name', header: 'Stage Name'},
            {
                id: 'stageProgress',
                header: 'Progress',
                accessorFn: (row) => `Stage ${row.stageNumber} / ${row.stageCount}`
            },
            {
                accessorKey: 'status',
                header: 'Status',
                // eslint-disable-next-line react/prop-types
                Cell: ({cell: {getValue}}) => <AdmicityStatus {...getApprovalStatusInfo(getValue())} />
            },
            {accessorKey: 'approvedBy', header: 'Approved By'},
            {
                accessorKey: 'approvedAt',
                header: 'Approved At',
                Cell: ({cell}) => (cell.getValue() ? formatDate(cell.getValue()) : '')
            },
            {accessorKey: 'approvalNote', header: 'Note', Cell: CustomCell},
        ],
        []
    );

    // Maria, 18 October 2026: on a staff profile, five columns with nothing that is not needed, in the order the form
    // goes through: the form, with the student or member of staff it is about underneath; its status; when it was
    // submitted and by whom; the stage; when it was approved, with the approver underneath only when it was someone else, and the note in the hover.
    /* eslint-disable react/prop-types */
    const SubLine = ({children}) => <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.25}}>{children}</Typography>;
    const personName = person => (person ? [person.forename, person.legalSurname].filter(Boolean).join(' ') : '');
    const profileColumns = useMemo(
        () => [
            {
                id: 'form',
                header: 'Form',
                Cell: ({row}) => (
                    <Box>
                        <Typography sx={{fontWeight: 500, lineHeight: 1.25}}>{row.original.request?.formName}</Typography>
                        <SubLine>{personName(row.original.request?.student) || personName(row.original.request?.employee)}</SubLine>
                    </Box>
                )
            },
            {
                id: 'status',
                header: 'Status',
                Cell: ({row}) => <AdmicityStatus {...getApprovalStatusInfo(row.original.status)}/>
            },
            {
                id: 'submitted',
                header: 'Submitted',
                Cell: ({row}) => (
                    <Box>
                        <Typography sx={{lineHeight: 1.25}}>{row.original.request?.editedAt ? formatDate(row.original.request.editedAt) : ''}</Typography>
                        <SubLine>{row.original.request?.editedBy?.fullname ?? ''}</SubLine>
                    </Box>
                )
            },
            {id: 'stage', header: 'Stage', Cell: ({row}) => row.original.stage?.name ?? ''},
            {
                id: 'approved',
                header: 'Approved',
                Cell: ({row}) => {
                    const {approvedAt, approvedBy, approvalNote, employee} = row.original;
                    if (!approvedAt) return '';
                    const someoneElse = approvedBy && approvedBy !== [employee?.name, employee?.surname].filter(Boolean).join(' ');
                    return (
                        <Tooltip title={approvalNote || ''}>
                            <Box>
                                <Typography sx={{lineHeight: 1.25}}>{formatDate(approvedAt)}</Typography>
                                {someoneElse && <SubLine>{approvedBy}</SubLine>}
                            </Box>
                        </Tooltip>
                    );
                }
            }
        ],
        []
    );

    // Maria, 19 October 2026: on a staff profile the box is joined to the Approvals tab
    const [widthRef, , measured, , , openTab] = useIndentUnderTab('tab-approvals');

    const rowActions = (row) => {
        const isFormActive = row.request?.form?.status === FORM_STATUSES.active;
        const isSubmitted = row.request?.status === FORM_REQUEST_STATUSES.submitted;
        const canShowHistory = (row.stageCount ?? 1) > 1;

        return [
            {
                label: 'Approve',
                disabled: () => !isFormActive || !isSubmitted || row.status === 'Approved',
                action: () => {
                    setSelectedAssignment(row);
                    setSelectedAction('Approved');
                    setNote('');
                    setDialogOpen(true);
                },
            },
            {
                label: 'Reject',
                disabled: () => !isFormActive || !isSubmitted || row.status === 'Approved' || row.status === 'Rejected',
                action: () => {
                    setSelectedAssignment(row);
                    setSelectedAction('Rejected');
                    setNote('');
                    setDialogOpen(true);
                },
            },
            {
                label: 'History',
                disabled: () => !canShowHistory,
                action: async () => {
                    setHistoryDialogOpen(true);
                    try {
                        await fetchHistory({requestId: row.request.id, currentStageNumber: row.stageNumber}, true);
                    } catch (e) {
                    }
                }
            }
        ];
    };

    // Maria, 18 October 2026: on a staff profile, the buttons at the end of each row in place of the three dots
    /* eslint-disable react/prop-types */
    const rowButtons = {
        // Maria, 19 October 2026: 'Actions', as on the other Forms lists; no line beside it, as the table never scrolls
        // sideways
        header: 'Actions',
        size: 60,
        muiTableBodyCellProps: {sx: {'&.MuiTableCell-root': {backgroundColor: '#fff', backgroundImage: 'none', opacity: 1}, width: '1% !important', minWidth: 'auto !important', whiteSpace: 'nowrap'}},
        muiTableHeadCellProps: {sx: {'&.MuiTableCell-root': {backgroundColor: '#e3f5f1', backgroundImage: 'none', opacity: 1, fontWeight: 600}, py: 1, lineHeight: 1.25, verticalAlign: 'middle', width: '1% !important', minWidth: 'auto !important'}},
        Cell: ({row}) => (
            <Box sx={{display: 'flex', gap: 1, justifyContent: 'flex-end'}}>
                {rowActions(row.original).filter(action => !action.disabled()).map(action => (
                    <Button
                        key={action.label}
                        size="small"
                        variant={action.label === 'Approve' ? 'contained' : action.label === 'Reject' ? 'outlined' : 'text'}
                        color={action.label === 'Reject' ? 'error' : 'primary'}
                        onClick={event => {
                            event.stopPropagation();
                            action.action();
                        }}
                    >
                        {action.label}
                    </Button>
                ))}
            </Box>
        )
    };
    /* eslint-enable react/prop-types */

    const handleRowClick = ({stage, request}) => {
        if (location.pathname.includes('/profiles/employees/')) {
            navigate(`/profiles/employees/${employeeId}/forms/${request.id}/approval`);
        } else {
            navigate(`/request-and-responses/approval/${stage.formId}/${request.id}`);
        }
    };

    return (
        <>
            {inProfile
                ? (
                    // Maria, 19 October 2026: from the left-hand edge, the full width of the page, as on the other sub-tabs
                    <Box ref={widthRef} sx={{...OUTLINED_BOX_SX, overflow: 'hidden', width: 'calc(100% - 16px)', mr: 2, mb: 2, visibility: measured ? 'visible' : 'hidden'}}>
                    {/* Maria, 18 October 2026: a border all round, joined to the Approvals tab like the other sub-tabs */}
                    <TopLineAroundTab openTab={openTab}/>
                    <Table
                        id={`approvalForms-${employeeId}`}
                        variant="elevation"
                        columns={profileColumns}
                        onRowClick={handleRowClick}
                        noRowsOverlay={{icon: null, text: 'No approvals pending.'}}
                        tableProps={{
                            ...COMPACT_TABLE_PROPS,
                            // each list is as tall as its rows, as on the other sub-tabs
                            muiTableContainerProps: {...COMPACT_TABLE_PROPS.muiTableContainerProps, sx: {flex: '0 0 auto'}},
                            // Maria, 18 October 2026: a narrower Form column, where long names wrap, and the other columns
                            // close together, with the spare width in the buttons column
                            muiTableHeadCellProps: props => withApprovalWidths(props, COMPACT_TABLE_PROPS.muiTableHeadCellProps(props)),
                            muiTableBodyCellProps: props => withApprovalWidths(props, COMPACT_TABLE_PROPS.muiTableBodyCellProps(props), true),
                            enableSorting: false,
                            enableFilters: false,
                            enableColumnFilters: false,
                            manualPagination: true,
                            enableFullScreenToggle: false,
                            enableHiding: false,
                            enableRowActions: true,
                            displayColumnDefOptions: {'mrt-row-actions': rowButtons}
                        }}
                    />
                    </Box>
                )
                : (
                    <Table
                        id={`approvalForms-${employeeId}`}
                        columns={columns}
                        onRowClick={handleRowClick}
                        rowActions={rowActions}
                        noRowsOverlay={{icon: null, text: 'No approvals pending.'}}
                        tableProps={{
                            enableSorting: false,
                            enableFilters: true,
                            manualFiltering: true,
                            manualPagination: true,
                        }}
                    />
                )}

            <AdmicityDialog
                open={dialogOpen}
                handleClose={closeNoteBox}
                title={selectedAction === 'Approved' ? 'Approval Note' : 'Rejection Note'}
                maxWidth="sm"
                actions={[
                    {label: 'Cancel', onClick: closeNoteBox},
                    {label: selectedAction, onClick: () => handleAction(selectedAction), disabled: false},
                ]}
            >
                <Stack spacing={2} mt={1}>
                    <TextField
                        multiline
                        fullWidth
                        minRows={3}
                        label="Optional Note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                </Stack>
            </AdmicityDialog>

            <AdmicityDialog
                open={historyDialogOpen}
                handleClose={() => setHistoryDialogOpen(false)}
                title="Approval History"
                maxWidth="md"
                actions={[
                    {label: 'Close', onClick: () => setHistoryDialogOpen(false)}
                ]}
            >
                <AdmicityTableV2
                    id={`approval-history-table-${employeeId}`}
                    columns={historyColumns}
                    data={historyRows}
                    totalItemCount={historyRows.length}
                    pagination={{pageIndex: 0, pageSize: historyRows.length}}
                    isLoading={isHistoryFetching}
                    isRefetching={false}
                    onPaginationModelChange={() => {
                    }}
                    tableProps={{
                        enableSorting: false,
                        enableFilters: false,
                        enablePagination: false,
                        enableBottomToolbar: false,
                        enableTopToolbar: false,
                        enableStickyHeader: true,
                        enableStickyFooter: false,
                        muiTablePaperProps: {
                            sx: {
                                display: 'block',
                            }
                        },
                        muiTableContainerProps: {
                            sx: {
                                maxHeight: 400,
                                overflow: 'auto',
                            }
                        }
                    }}
                    noRowsOverlay={{icon: null, text: isHistoryFetching ? 'Loading…' : 'No history yet.'}}
                />
                {isHistoryError && (
                    <div style={{paddingTop: 8, color: '#b00020'}}>
                        Failed to load history{historyError?.data?.title ? `: ${historyError.data.title}` : ''}.
                    </div>
                )}
            </AdmicityDialog>

        </>
    );
};

ApprovalFormsTable.propTypes = {
    employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    inProfile: PropTypes.bool,
};

export default ApprovalFormsTable;
